import { heroCards, statusFeed, type HeroCard } from '@/mocks/home';
import StatusPill from './StatusPill';

function HeroCardView({ card }: { card: HeroCard }) {
  return (
    <article
      className={`group relative overflow-hidden rounded-lg ${
        card.wide ? 'col-span-2 h-[220px] md:h-[280px]' : 'h-[240px] md:h-[320px]'
      }`}
    >
      <img
        src={card.image}
        alt={`黑料官网 ${card.title} 封面`}
        title={`黑料官网 ${card.label} 备用地址指引`}
        className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/10"></div>

      <div className="relative flex h-full flex-col justify-between p-4 md:p-5">
        <div className="flex items-start justify-between gap-3">
          <span className="rounded-full bg-background-50/95 px-3 py-1 text-xs font-medium text-foreground-800">
            {card.label}
          </span>
          <StatusPill label={card.status} tone={card.tone} />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-background-50 md:text-xl">{card.title}</h3>
          <p className="mt-1.5 hidden text-sm leading-relaxed text-background-200 sm:block">{card.desc}</p>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-background-300">
            <i className="ri-time-line"></i>
            {card.meta}
          </p>
        </div>
      </div>
    </article>
  );
}

export default function Hero() {
  const ticker = statusFeed.map((item) => `${item.tag} · ${item.title}`);

  return (
    <section id="top" className="relative overflow-hidden bg-background-50 pb-16 pt-24 md:pb-24 md:pt-32">
      <div className="pointer-events-none absolute -left-24 top-16 h-72 w-72 rounded-full bg-accent-200/40 blur-3xl"></div>
      <div className="pointer-events-none absolute right-[-60px] top-48 h-80 w-80 rounded-full bg-primary-200/40 blur-3xl"></div>
      <div className="grain-overlay pointer-events-none absolute inset-0 opacity-50"></div>

      <div className="relative mx-auto w-full max-w-[1280px] px-4 md:px-6">
        <div className="mb-10 flex items-center gap-3 overflow-hidden rounded-md border border-background-200 bg-background-100/80 px-4 py-2.5">
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-primary-600">
            <i className="ri-flashlight-fill text-sm"></i>
            线路快讯
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee items-center gap-8">
              {[...ticker, ...ticker].map((text, index) => (
                <span key={index} className="whitespace-nowrap text-xs text-foreground-600">
                  {text}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-xs font-medium tracking-wide text-primary-700">
              <span className="h-2 w-2 animate-pulse-dot rounded-full bg-primary-500"></span>
              官方访问指引 · 地址实时同步
            </span>

            <h1 className="mt-6 animate-fade-up font-heading text-5xl font-black leading-[1.1] text-foreground-950 md:text-6xl">
              黑料官网
            </h1>

            <p className="mt-5 animate-fade-up font-heading text-xl font-semibold leading-snug text-foreground-800 md:text-2xl">
              官网怎么进 · 打不开 · 备用 · 换地址，一页讲清楚
            </p>

            <p className="mt-5 max-w-xl animate-fade-up text-base leading-relaxed text-foreground-600">
              遇到黑料官网打不开或无法访问？本页汇总官网怎么进、备用地址与换地址指引，帮你快速解决访问异常，随时获取最新内容。
            </p>

            <div className="mt-8 flex animate-fade-up flex-col gap-3 sm:flex-row">
              <a
                href="#addresses"
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
              >
                <i className="ri-links-line text-base"></i>
                查看备用地址
              </a>
              <a
                href="#guide"
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-guide-line text-base"></i>
                查看访问指引
              </a>
            </div>

            <div className="mt-10 grid animate-fade-up grid-cols-3 gap-4 border-t border-background-200 pt-6">
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">5</p>
                <p className="mt-1 text-xs text-foreground-500">可切换线路</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">24h</p>
                <p className="mt-1 text-xs text-foreground-500">状态持续监测</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">10min</p>
                <p className="mt-1 text-xs text-foreground-500">地址同步频率</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {heroCards.map((card) => (
                <HeroCardView key={card.id} card={card} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}